<template>
    <div class="ahe-page">
        <!-- Header -->
        <div class="ahe-header">
            <div class="ahe-header-left">
                <button class="ahe-back" @click="cancelEdit"><i class="fas fa-arrow-left"></i></button>
                <div>
                    <h1 class="ahe-title">{{ editMode ? "Update Holiday" : "Create Holiday" }}</h1>
                    <p class="ahe-subtitle">{{ editMode ? "Modify an existing holiday record" : "Add a new holiday to the calendar" }}</p>
                </div>
            </div>
        </div>

        <!-- Form Card -->
        <div class="ahe-card" v-if="!editMode || editData">
            <div class="ahe-card__header">
                <i class="fas fa-calendar-plus"></i>
                <span>Holiday Details</span>
            </div>
            <div class="ahe-card__body">
                <div class="ahe-grid">
                    <div class="ahe-field">
                        <custom-input v-model="name" label="Holiday Name*" />
                    </div>
                    <div class="ahe-field">
                        <custom-input v-model="description" label="Description" type="text" />
                    </div>
                    <div class="ahe-field">
                        <custom-input v-model="startDate" label="Start Date*" type="date" />
                    </div>
                    <div class="ahe-field">
                        <custom-input v-model="endDate" label="End Date*" type="date" />
                    </div>
                    <div class="ahe-field">
                        <label class="ahe-label"><i class="fas fa-tag"></i> Holiday Type*</label>
                        <SelectDropdown
                            v-if="!editMode || editData"
                            :id="`form-work-location`"
                            :options="attendanceHolidayTypes.map((item) => ({ id: item.id, title: item.name }))"
                            :multiple="false"
                            field="form-work-location"
                            :value="holidayTypeId"
                            @change="holidayTypeId = $event"
                        />
                    </div>
                </div>
            </div>

            <!-- Location Section -->
            <div class="ahe-card__section" v-if="holidayTypeId && (!editMode || editData)">
                <div class="ahe-card__section-title">
                    <i class="fas fa-map-marker-alt"></i> Location
                </div>
                <CountryStateCitySelector
                    :countryId="countryId"
                    :stateId="stateId"
                    :cityId="cityId"
                    :hideCountry="hideCountryName"
                    :disableCountry="disableCountry"
                    :disableCity="true"
                    :disableState="hideState"
                    @country="handleCountryUpdate"
                    @state="stateId = $event"
                    @city="cityId = $event"
                />
            </div>

            <!-- Footer Actions -->
            <div class="ahe-card__footer">
                <button class="ahe-btn ahe-btn--ghost" @click="cancelEdit">
                    <i class="fas fa-times"></i> Cancel
                </button>
                <button class="ahe-btn ahe-btn--primary" @click="createOrUpdateWorkLocation" :disabled="processing">
                    <i class="fas fa-spinner fa-spin" v-if="processing"></i>
                    <i class="fas fa-save" v-else></i>
                    {{ processing ? "Saving..." : editMode ? "Update Holiday" : "Create Holiday" }}
                </button>
            </div>
        </div>

        <!-- Loading -->
        <div class="ahe-loading" v-else>
            <i class="fas fa-circle-notch fa-spin"></i>
            <span>Loading holiday data...</span>
        </div>
    </div>
</template>

<script>
import { http, serverURL } from "@/utils/http"
import { createField, createColumn } from "@/utils/common"
import CustomInput from "@/components/crud/custom-input.vue"
import Button from "@/components/UI/Button.vue"
import SelectDropdown from "@/components/crud/SelectDropdown.vue"
import OldImageView from "@/components/UI/OldImageView.vue"
import CountryStateCitySelector from "@/components/UI/CountryStateCitySelector.vue"
import { mapState } from "vuex"
import { formatIsoDate } from "@/utils/common"

export default {
    data() {
        return {
            serverURL,
            processing: false,
            loading: true,

            // master ids
            name: "",
            description: "",
            startDate: formatIsoDate(),
            endDate: formatIsoDate(),
            cityId: "",
            stateId: "",
            countryId: "",

            holidayTypeId: "",
            // documents
            // edit related fields
            editMode: true,
            editData: null,
            attendanceHolidayTypes: [],
        }
    },
    props: { videoId: { type: String, default: "" } },
    components: { CountryStateCitySelector, OldImageView, CustomInput, SelectDropdown, Button },
    async mounted() {
        await this.fetchAttendanceHolidayTypes()
        await this.initIfEditMode()
    },
    computed: {
        ...mapState("misc", ["countries"]),
        hideState() {
            const regionalHoliday = this.attendanceHolidayTypes.find((item) =>
                item.name.toLowerCase().includes("regional")
            )
            return regionalHoliday && this.holidayTypeId != regionalHoliday.id
        },
    },

    methods: {
        handleCountryUpdate(update) {
            if (update) {
                this.countryId = update
            }
        },
        cancelEdit() {
            this.$router.push("/attendance-holidays")
        },
        async initIfEditMode() {
            const id = this.$route.params.id
            if (id) {
                this.editMode = true
                const res = await http.get("/attendance-holidays/" + id)
                this.editData = res.data.result

                // set the defaults to each field.
                if (this.editData) {
                    this.name = this.editData.name || ""
                    this.description = this.editData.description || ""
                    this.startDate = this.editData.startDate || ""
                    this.endDate = this.editData.endDate || ""
                    this.address = this.editData.address || ""
                    this.stateId = this.editData.stateId || ""
                    this.countryId = this.editData.countryId || ""
                    this.holidayTypeId = this.editData.holidayTypeId || ""
                }
            } else {
                this.editMode = false
            }
        },
        async createOrUpdateWorkLocation() {
            if (this.processing) return

            if (!this.name) {
                return this.$toast.error("Please enter holiday name")
            }
            if (!this.startDate || !this.endDate) {
                return this.$toast.error("Start Date and End date are required!")
            }
            if (!this.holidayTypeId) {
                return this.$toast.error("Please select holiday type!")
            }
            if (!this.countryId) {
                return this.$toast.error("Please select country!")
            }
            if (!this.hideState && !this.stateId) {
                return this.$toast.error("Please select state!")
            }
            if (this.startDate > this.endDate) {
                return this.$toast.error("Start Date cannot be greater than End date!")
            }

            this.processing = true
            const formDataObj = {
                name: this.name,
                description: this.description,
                startDate: formatIsoDate(this.startDate),
                endDate: formatIsoDate(this.endDate),
                stateId: this.stateId || null,
                countryId: this.countryId,
                holidayTypeId: this.holidayTypeId,
            }
            try {
                if (this.editMode) {
                    await http.patch(`/attendance-holidays/${this.editData.id}`, formDataObj)
                } else {
                    await http.post("/attendance-holidays", formDataObj)
                }
                this.$toast.success(
                    this.editMode
                        ? "Attendance Holiday Updated Successfully"
                        : "Attendance Holiday Created Successfully"
                )
                window.location.href = "/attendance-holidays"
            } catch (err) {
                console.log("error in creating Attendance Holiday", err)
                this.$toast.error(
                    (err.response && err.response.data && err.response.data.message) ||
                        "Some error occured"
                )
            }
            this.processing = false
        },
        async fetchAttendanceHolidayTypes() {
            const res = await http.get("/attendance-holiday-types")
            this.attendanceHolidayTypes = res.data.result
        },
    },
    watch: {
        // holidayTypeId(e) {
        //     if (e == this.domesticTypeId) {
        //         this.country = this.countries.find((item) => item.title == "India").id
        //     }
        // },
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

.ahe-page {
    animation: aheFade .25s ease;
}
@keyframes aheFade {
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: translateY(0); }
}

/* ── Header ── */
.ahe-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 12px;
}
.ahe-header-left {
    display: flex;
    align-items: center;
    gap: 12px;
}
.ahe-back {
    width: 36px;
    height: 36px;
    border-radius: $radiusSm;
    border: 1px solid $gray200;
    background: white;
    color: $gray600;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 14px;
    transition: all $transitionFast;

    &:hover {
        background: $gray50;
        border-color: $gray300;
        color: $gray800;
    }
}
.ahe-title {
    font-size: 20px;
    font-weight: 700;
    color: $gray800;
    margin: 0;
}
.ahe-subtitle {
    font-size: 13px;
    color: $gray400;
    margin: 2px 0 0;
}

/* ── Card ── */
.ahe-card {
    background: white;
    border: 1px solid $gray100;
    border-radius: $radiusMd;
    box-shadow: $shadowSm;
    overflow: visible;
}
.ahe-card__header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px 20px;
    border-bottom: 1px solid $gray100;
    background: $gray50;
    font-size: 14px;
    font-weight: 700;
    color: $gray800;

    i { color: $primaryColor; font-size: 14px; }
}
.ahe-card__body {
    padding: 20px;
}
.ahe-card__section {
    padding: 0 20px 16px;
}
.ahe-card__section-title {
    font-size: 12px;
    font-weight: 600;
    color: $gray500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
    gap: 6px;
    padding-top: 12px;
    border-top: 1px solid $gray100;

    i { font-size: 11px; color: $gray400; }
}
.ahe-card__footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    padding: 14px 20px;
    border-top: 1px solid $gray100;
    background: $gray50;
}

/* ── Grid ── */
.ahe-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
}
.ahe-field {
    min-width: 0;
}
.ahe-label {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    font-weight: 600;
    color: $gray500;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    margin-bottom: 4px;

    i { font-size: 10px; color: $gray400; }
}

/* ── Buttons ── */
.ahe-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 9px 20px;
    border-radius: $radiusSm;
    font-size: 13px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    border: none;
    transition: all $transitionFast;

    &:disabled {
        opacity: 0.55;
        cursor: not-allowed;
    }

    &--primary {
        background: $primaryColor;
        color: white;
        &:hover:not(:disabled) { background: $primaryDark; }
    }
    &--ghost {
        background: white;
        color: $gray600;
        border: 1px solid $gray200;
        &:hover { background: $gray50; color: $gray800; }
    }
}

/* ── Loading ── */
.ahe-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 64px 20px;
    background: white;
    border: 1px solid $gray100;
    border-radius: $radiusMd;
    box-shadow: $shadowSm;
    font-size: 13px;
    color: $gray400;

    i { font-size: 18px; color: $primaryColor; }
}

/* ── Responsive ── */
@media (max-width: 640px) {
    .ahe-grid { grid-template-columns: 1fr; }
    .ahe-card__footer { flex-direction: column-reverse; }
    .ahe-btn { width: 100%; justify-content: center; }
}
</style>
